import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Single axios instance with credentials so httpOnly cookies are sent.
export const http = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: 120000,
});

// ---------- Analysis ----------
export async function analyzeChart(imageBase64) {
  const { data } = await http.post("/analyze", { image_base64: imageBase64 });
  return data;
}

// ---------- Auth ----------
export async function apiRegister({ email, password, name }) {
  const { data } = await http.post("/auth/register", { email, password, name });
  return data;
}
export async function apiLogin({ email, password }) {
  const { data } = await http.post("/auth/login", { email, password });
  return data;
}
export async function apiLogout() {
  const { data } = await http.post("/auth/logout");
  return data;
}
export async function apiMe() {
  const { data } = await http.get("/auth/me");
  return data;
}
export async function apiGoogleExchange(sessionId) {
  const { data } = await http.post("/auth/google/session", { session_id: sessionId });
  return data;
}
export async function apiForgotPassword(email) {
  const { data } = await http.post("/auth/forgot-password", { email });
  return data;
}
export async function apiResetPassword({ token, new_password }) {
  const { data } = await http.post("/auth/reset-password", { token, new_password });
  return data;
}
export async function apiVerifyEmail(token) {
  const { data } = await http.post("/auth/verify-email", { token });
  return data;
}
export async function apiResendVerification() {
  const { data } = await http.post("/auth/resend-verification");
  return data;
}
export async function apiSetPassword({ new_password, current_password }) {
  const body = { new_password };
  if (current_password) body.current_password = current_password;
  const { data } = await http.post("/auth/set-password", body);
  return data;
}

// ---------- Membership ----------
export async function apiMembership() {
  const { data } = await http.get("/membership");
  return data;
}
export async function apiCreateCheckout(originUrl) {
  const { data } = await http.post("/billing/checkout/session", {
    package_id: "membership_monthly",
    origin_url: originUrl,
  });
  return data;
}
export async function apiCheckoutStatus(sessionId) {
  const { data } = await http.get(`/billing/checkout/status/${sessionId}`);
  return data;
}
export async function apiBillingPortal(returnUrl) {
  const { data } = await http.post("/billing/portal", { return_url: returnUrl });
  return data;
}
export async function apiCreditPackages() {
  const { data } = await http.get("/billing/credits/packages");
  return data;
}
export async function apiCreateCreditCheckout(packageId, originUrl) {
  const { data } = await http.post("/billing/credits/checkout", {
    package_id: packageId, origin_url: originUrl,
  });
  return data;
}

// ---------- Weekly Updates ----------
export async function apiWeeklyUpdates() {
  const { data } = await http.get("/weekly-updates");
  return data;
}
export async function apiWeeklyUpdate(id) {
  const { data } = await http.get(`/weekly-updates/${id}`);
  return data;
}

// ---------- Market Analysis (Phase 1: pairs + candles) ----------
export async function apiMarketPairs({ exchange = "binance", market_type = "spot" } = {}) {
  const p = new URLSearchParams({ exchange, market_type });
  const { data } = await http.get(`/market-analysis/pairs?${p.toString()}`);
  return data;
}
export async function apiMarketCandles({ exchange = "binance", market_type = "spot", symbol, timeframe, limit = 300 }) {
  const p = new URLSearchParams({ exchange, market_type, symbol, timeframe, limit: String(limit) });
  const { data } = await http.get(`/market-analysis/candles?${p.toString()}`);
  return data;
}
export async function apiAnalyzeMarket({
  exchange = "binance",
  market_type = "spot",
  symbol,
  timeframe,
  limit = 300,
}) {
  const { data } = await http.post("/market-analysis/analyze", {
    exchange, market_type, symbol, timeframe, limit,
  });
  return data;
}

// ---------- My analyses history ----------
export async function apiMyAnalyses({ source, limit = 20 } = {}) {
  const p = new URLSearchParams();
  if (source) p.set("source", source);
  p.set("limit", String(limit));
  const { data } = await http.get(`/analyses/mine?${p.toString()}`);
  return data;
}

// ---------- Admin: members + push messages ----------
export async function apiAdminOverview() {
  const { data } = await http.get("/admin/overview");
  return data;
}
export async function apiAdminMembers({ search, status, limit = 100 } = {}) {
  const p = new URLSearchParams();
  if (search) p.set("search", search);
  if (status) p.set("status", status);
  p.set("limit", String(limit));
  const { data } = await http.get(`/admin/members?${p.toString()}`);
  return data;
}
export async function apiAdminAdjustCredits(userId, delta, reason) {
  const { data } = await http.post(`/admin/members/${userId}/credits/adjust`, { delta, reason });
  return data;
}
export async function apiAdminStripeCheck(userId) {
  const { data } = await http.get(`/admin/members/${userId}/stripe-check`);
  return data;
}
export async function apiAdminStripeCheckApply(userId) {
  const { data } = await http.post(`/admin/members/${userId}/stripe-check/apply`);
  return data;
}
export async function apiAdminPushMessages() {
  const { data } = await http.get("/admin/push-messages");
  return data;
}
export async function apiAdminSendPushMessage({ subject, body_html }) {
  const { data } = await http.post("/admin/push-messages", { subject, body_html });
  return data;
}

// ---------- Expert Review ----------
export async function apiExpertCredits() {
  const { data } = await http.get("/expert-review/credits");
  return data;
}
export async function apiExpertSubmit({ asset, question, image_base64, image_mime }) {
  const { data } = await http.post("/expert-review", { asset, question, image_base64, image_mime });
  return data;
}
export async function apiExpertList() {
  const { data } = await http.get("/expert-review/requests");
  return data;
}

// Helper: turn FastAPI error responses into a clean string.
export function formatApiError(err) {
  const d = err?.response?.data?.detail;
  if (d == null) return err?.message || "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(d);
}
